/**
 * Visual + timing constants for the bell and its panel.
 *
 * Colours come from the MUI palette (`statusSoft.*` / `statusInk.*`,
 * `surface.*`, `divider`) at every call site, never a literal here — only
 * layout numbers and their reasons live in this file, per `web/AGENTS.md`.
 */
import { radius, shadow } from '../../theme/tokens';

// ── The bell (SPEC "Bell") ───────────────────────────────────────────────────

/** Same 32px bordered square as the theme toggle beside it in `TopBar`. */
export const BELL_BUTTON_SIZE = 32;
export const BELL_ICON_FONT_SIZE = 15;
/** The count pill floats over the button's corner rather than pushing layout. */
export const BADGE_SIZE = 16;
/**
 * On the 0.7rem floor, like every other size in this file.
 *
 * `web/AGENTS.md` says "minimum font size ANYWHERE", and a two-digit unread
 * count is one of the smaller things a reader has to actually read — being a
 * number rather than prose is not a reason to shrink it. 11.2px inside the
 * 16px pill still leaves room for "99+" without the glyphs touching the edge.
 */
export const BADGE_FONT_SIZE = '0.7rem';
/** Above this the pill would overflow the bell's own square — shown as "99+". */
export const BADGE_MAX_DISPLAY_COUNT = 99;

// ── Polling (AC-28) ───────────────────────────────────────────────────────────

/**
 * How often the count is re-read while the tab is visible.
 *
 * 30s: frequent enough that a badge feels live without the endpoint being hit
 * on every render, and cheap enough that a background browsing session across
 * many tabs never notices it — it pauses entirely on `visibilitychange` (AC-28)
 * on top of that.
 */
export const UNREAD_COUNT_POLL_INTERVAL_MS = 30_000;

// ── The panel (SPEC "Panel") ──────────────────────────────────────────────────

/**
 * Below this width the panel is a full-width bottom sheet rather than a
 * popover anchored to the bell — a popover this wide would be clipped by the
 * viewport at 375 (AC-31). Matches the breakpoint the SPEC states explicitly
 * rather than a nearby MUI breakpoint (`sm` is 600, `md` is 900).
 */
export const PANEL_POPOVER_MIN_WIDTH = 768;
/** Popover width at ≥768 — enough for a title line and a relative time without wrapping. */
export const PANEL_WIDTH = 392;
/** Max height with the list scrolling inside its own container (SPEC). */
export const PANEL_MAX_HEIGHT = 480;
export const PANEL_HEADER_HEIGHT = 52;
export const PANEL_BORDER_RADIUS = radius.lg;
export const PANEL_SHADOW = shadow.light.pop;
export const PANEL_SHADOW_DARK = shadow.dark.pop;

// ── Items ─────────────────────────────────────────────────────────────────────

export const ITEM_PADDING_Y = 1.25;
export const ITEM_PADDING_X = 1.75;
/**
 * Unread's left accent border. Colour alone fails at both ends of the
 * contrast range and in dark mode, so an unread item is ALSO given a stronger
 * title weight (SPEC) — this is only the first of the two signals.
 */
export const UNREAD_ACCENT_WIDTH = 3;
export const ITEM_TITLE_FONT_SIZE = '0.8125rem';
export const ITEM_BODY_FONT_SIZE = '0.75rem';
/**
 * The relative time under an item, and the empty / loading / error sentences.
 *
 * Sits ON the 0.7rem floor in `web/AGENTS.md`, not under it — 0.6875rem (11px)
 * was a shade below and had to go up. These carry content a reader actually has
 * to parse ("3 hours ago", "You're all caught up"), so they are not in the
 * decorative class the floor makes allowances for. Matches `FONT_TABLE_META` in
 * the sibling notifications tokens so the two panels read as one system.
 */
export const ITEM_META_FONT_SIZE = '0.71875rem';

// ── Bulk dismiss (IAN-53, AC-50) ──────────────────────────────────────────────

/** "Clear everything older than a month" — a calendar month approximated as 30 days. */
export const BULK_DISMISS_OLDER_THAN_DAYS = 30;

// ── Typography shared with the rest of the panel ─────────────────────────────

/** Never below the 0.7rem floor in `web/AGENTS.md`. */
export const FONT_PANEL_TITLE = '0.875rem';
export const FONT_ACTION = '0.75rem';
